import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Ban, CheckCircle2, Clock, Download, PencilLine, Send, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { fmtDateTime } from "@/lib/format";
import { fmtMinutes } from "@/lib/interventions";
import { BillingStatus, EconStatus, ReportStatus, minToHours } from "@/lib/reports";
import { Panel, SectionLabel } from "@/components/common/ui-kit";
import { DocumentsPanel } from "@/components/documents/DocumentsPanel";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { TimelineList } from "@/components/crm/TimelineList";
import { ReportEditor } from "@/components/reports/ReportEditor";
import { LinesEditor } from "@/components/reports/LinesEditor";
import { ChargePanel } from "@/components/reports/ChargePanel";
import { ActivityLines } from "@/components/reports/ActivityLines";
import { BillingPanel } from "@/components/reports/BillingPanel";
import { SendEmailButton, downloadPdf } from "@/components/reports/SendEmailButton";
import { BillableDialog, ConfirmDialog, RateDialog, ReasonDialog } from "@/components/reports/ReportDialogs";

const NonBillable = ({ rep, onDone }) => {
  const [busy, setBusy] = useState(false);
  const toggle = async (v) => {
    setBusy(true);
    try { await api.put(`/service-reports/${rep.id}/non-billable`, { value: v }); toast.success(v ? "Rapportino non fatturabile: valore azzerato alla conferma" : "Rapportino di nuovo fatturabile"); onDone(); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center justify-between py-1.5" data-testid="report-non-billable-row">
      <span className="text-slate-500">Non fatturabile<span className="block text-[11px] text-slate-400">valore zero, niente fatturazione né pacchetto ore</span></span>
      {rep.can.billable ? <Switch checked={!!rep.non_billable} disabled={busy} onCheckedChange={toggle} data-testid="report-non-billable-switch" />
        : <b data-testid="report-non-billable-value">{rep.non_billable ? "Sì" : "No"}</b>}
    </div>
  );
};

const Times = ({ rep, onBillable, onChanged }) => (
  <Panel className="p-5" data-testid="report-times-summary">
    <SectionLabel>Tempi</SectionLabel>
    <div className="mt-2 divide-y divide-line/60 text-sm">
      <div className="flex justify-between py-1.5"><span className="text-slate-500">Tempo effettivo</span><b data-testid="report-effective-value">{fmtMinutes(rep.effective_minutes)}</b></div>
      <div className="flex justify-between py-1.5"><span className="text-slate-500">Proposto dal tecnico</span><b>{rep.proposed_billable_minutes == null ? "—" : fmtMinutes(rep.proposed_billable_minutes)}</b></div>
      <div className="flex items-center justify-between py-1.5"><span className="text-slate-500">Fatturabile definitivo</span>
        <span className="flex items-center gap-2"><b data-testid="report-billable-value">{rep.billable_minutes == null ? "Da definire" : fmtMinutes(rep.billable_minutes)}</b>
          {rep.billable_manual && <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[11px] text-amber-700" data-testid="report-billable-manual">corretto a mano</span>}
          {rep.can.billable && <Button size="sm" variant="outline" className="gap-1" onClick={onBillable} data-testid="report-billable-button"><Clock className="h-4 w-4" />Imposta</Button>}</span></div>
      <NonBillable rep={rep} onDone={onChanged} />
    </div>
  </Panel>
);

const ActionBar = ({ rep, open, submit, busy }) => {
  const c = rep.can;
  const btn = (k, I, l, extra = {}) => <Button key={k} onClick={() => open(k)} className="gap-2" data-testid={`report-action-${k}`} {...extra}><I className="h-4 w-4" />{l}</Button>;
  return (
    <div className="fixed inset-x-0 bottom-0 z-30 flex flex-wrap justify-end gap-2 border-t border-line bg-white/95 p-3 backdrop-blur md:static md:border-0 md:bg-transparent md:p-0" data-testid="report-actions">
      {rep.status === "confermato" && <Button variant="outline" onClick={() => downloadPdf(`/service-reports/${rep.id}`, `${rep.number}.pdf`)} className="gap-2" data-testid="report-download-pdf"><Download className="h-4 w-4" />Scarica PDF</Button>}
      {rep.status === "confermato" && <SendEmailButton path={`/service-reports/${rep.id}`} filename={`${rep.number}.pdf`} />}
      {c.submit && <Button onClick={submit} disabled={busy} className="h-11 flex-1 gap-2 md:h-10 md:flex-none" data-testid="report-action-submit"><Send className="h-4 w-4" />INVIA RAPPORTINO</Button>}
      {c.return && btn("return", Undo2, "Rimanda al tecnico", { variant: "outline" })}
      {c.confirm && btn("confirm", CheckCircle2, c.fast ? "Conferma rapportino" : "Conferma")}
      {c.correct && btn("correct", PencilLine, "Rettifica", { variant: "outline" })}
      {c.cancel && btn("cancel", Ban, "Annulla", { variant: "outline" })}
    </div>
  );
};

export default function ReportDetail() {
  const { recordId } = useParams();
  const { can } = useAuth();
  const econ = can("reports:economics");
  const [rep, setRep] = useState(null);
  const [preview, setPreview] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(false);
  const flush = useRef(null);
  const refreshAux = useCallback(() => {
    if (econ) api.get(`/service-reports/${recordId}/preview`).then((r) => setPreview(r.data)).catch(() => {});
    api.get(`/service-reports/${recordId}/timeline`).then((r) => setTimeline(r.data.items)).catch(() => {});
  }, [recordId, econ]);
  const load = useCallback(() => {
    api.get(`/service-reports/${recordId}`).then((r) => setRep(r.data)).catch((e) => setRep({ error: errorMessage(e) }));
    refreshAux();
  }, [recordId, refreshAux]);
  useEffect(() => { load(); }, [load]);
  const submit = async () => {
    setBusy(true);
    try { if (flush.current && !(await flush.current())) return; await api.post(`/service-reports/${rep.id}/submit`); toast.success("Rapportino inviato"); load(); }
    catch (e) { toast.error(errorMessage(e)); } finally { setBusy(false); }
  };
  if (!rep) return <p className="text-sm text-slate-500">Caricamento…</p>;
  if (rep.error) return <p className="text-sm text-red-600" role="alert" data-testid="report-error">{rep.error}</p>;
  const close = (o) => !o && setModal(null);
  const key = `${rep.id}-${rep.status}-${rep.version}`;
  const post = (path) => (reason) => api.post(`/service-reports/${rep.id}/${path}`, { reason });
  return (
    <div data-testid="report-detail" className="pb-28 md:pb-0">
      <Link to={`/interventi/${rep.intervention_id}`} className="mb-3 inline-flex items-center gap-1 text-sm text-slate-500 hover:text-ink" data-testid="report-back"><ArrowLeft className="h-4 w-4" />{rep.intervention?.number} · {rep.intervention?.title}</Link>
      <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <p className="font-mono text-xs font-semibold text-slate-400" data-testid="report-number">{rep.number} · versione {rep.version}{rep.period ? ` · rapportino mensile ${rep.period}` : ""}</p>
          <h1 className="mt-1 font-display text-2xl font-bold text-ink sm:text-3xl">{rep.company_name}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-1.5"><ReportStatus status={rep.status} testId="report-status" /><EconStatus status={rep.economic_status} testId="report-econ-status" /><BillingStatus rep={rep} testId="report-header-billing" />
            <span className="text-xs text-slate-500">Tecnico: {rep.technician_name}{rep.confirmed_at ? ` · confermato da ${rep.confirmed_by_name} il ${fmtDateTime(rep.confirmed_at)}` : ""}</span></div>
          {rep.status === "bozza" && rep.returned_reason && <p className="mt-2 text-sm text-amber-700" data-testid="report-returned-reason">Rimandato: {rep.returned_reason}</p>}
          {rep.status === "annullato" && rep.cancel_reason && <p className="mt-2 text-sm text-slate-600">Annullato: {rep.cancel_reason}</p>}
        </div>
        <ActionBar rep={rep} open={async (k) => { if (k === "confirm" && rep.can.edit && flush.current && !(await flush.current())) return; setModal(k); }} submit={submit} busy={busy} />
      </div>
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-12">
        <div className="space-y-5 lg:col-span-7">
          <BillingPanel rep={rep} onDone={load} />
          <DocumentsPanel entityType="report" entityId={rep.id} />
          <ActivityLines rep={rep} onChanged={load} />
          <ReportEditor key={key} rep={rep} flushRef={flush} onSaved={load} />
          <LinesEditor key={`m-${key}`} rep={rep} field="materials" kind="prodotto" title="Materiali" onSaved={load} />
          <LinesEditor key={`s-${key}`} rep={rep} field="services" kind="servizio" title="Servizi" onSaved={load} />
        </div>
        <div className="space-y-5 lg:col-span-5">
          <Times rep={rep} onBillable={() => setModal("billable")} onChanged={load} />
          {econ && <ChargePanel rep={rep} preview={preview} onRate={() => setModal("rate")} />}
          {rep.corrections?.length > 0 && <Panel className="p-5" data-testid="report-corrections"><SectionLabel>Rettifiche</SectionLabel>
            <ul className="mt-2 space-y-1 text-sm">{rep.corrections.map((c, i) => <li key={i} className="text-slate-600">v{c.from_version} · {fmtDateTime(c.at)} · {c.reason}</li>)}</ul></Panel>}
          <Panel className="p-5"><SectionLabel>Timeline</SectionLabel><div className="mt-4"><TimelineList items={timeline} /></div></Panel>
        </div>
      </div>
      <BillableDialog open={modal === "billable"} onOpenChange={close} rep={rep} onDone={load} />
      <RateDialog open={modal === "rate"} onOpenChange={close} rep={rep} onDone={load} />
      <ConfirmDialog open={modal === "confirm"} onOpenChange={close} rep={rep} preview={preview} onDone={load} />
      <ReasonDialog open={modal === "return"} onOpenChange={close} title="Rimanda al tecnico" saveLabel="Rimanda" onSubmit={post("return")} onDone={load} />
      <ReasonDialog open={modal === "correct"} onOpenChange={close} title="Avvia rettifica" saveLabel="Avvia rettifica" onSubmit={post("correct")} onDone={load}
        desc="Il consuntivo viene stornato e l'eventuale consumo del pacchetto ripristinato. Dopo le modifiche serve una nuova conferma." />
      <ReasonDialog open={modal === "cancel"} onOpenChange={close} title="Annulla rapportino" saveLabel="Annulla rapportino" danger onSubmit={post("cancel")} onDone={load}
        desc={rep.status === "confermato" ? "Il consuntivo viene stornato e l'eventuale consumo del pacchetto ripristinato." : undefined} />
    </div>
  );
}
